import { localizedText } from "../helpers/localization"
import { Controller } from "@hotwired/stimulus"

const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000

export default class extends Controller {
  static targets = ["joinDateText", "joinDatePicker", "status", "result", "age", "totalDays", "totalWeeks", "dayCreated", "nextAnniversary"]

  calculate(event) {
    event.preventDefault()
    const joined = this.#parseDate(this.joinDateTextTarget.value || this.joinDatePickerTarget.value)
    const today = this.#today()

    if (!joined) return this.#showError(localizedText(this.element, "invalid_join_date"))
    if (joined > today) return this.#showError(localizedText(this.element, "future_join_date"))

    const age = this.#ageBetween(joined, today)
    const totalDays = this.#daysBetween(joined, today)
    const nextAnniversary = this.#nextAnniversary(joined, today)

    this.ageTarget.textContent = localizedText(this.element, "channel_age", {
      years: localizedText(this.element, "years", { count: age.years }),
      months: localizedText(this.element, "months", { count: age.months }),
      days: localizedText(this.element, "days", { count: age.days })
    })
    this.totalDaysTarget.textContent = this.#number(totalDays)
    this.totalWeeksTarget.textContent = this.#number(Math.floor(totalDays / 7))
    this.dayCreatedTarget.textContent = localizedText(this.element, "created_weekday", { day: joined.toLocaleDateString(this.element.ownerDocument.body.lang, { weekday: "long", timeZone: "UTC" }) })
    this.nextAnniversaryTarget.textContent = localizedText(this.element, "days_remaining", { count: this.#daysBetween(today, nextAnniversary) })
    this.statusTarget.textContent = ""
    this.resultTarget.classList.remove("hidden")
  }

  recalculate() {
    this.resultTarget.classList.add("hidden")
    this.statusTarget.textContent = ""
    this.joinDateTextTarget.focus()
  }

  #parseDate(value) {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-").map(Number)
      return this.#validDate(year, month, day)
    }

    const match = value.trim().match(/^([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})$/)
    if (!match) return null

    const month = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"].indexOf(match[1].slice(0, 3).toLowerCase()) + 1
    return month > 0 ? this.#validDate(Number(match[3]), month, Number(match[2])) : null
  }

  #validDate(year, month, day) {
    const date = new Date(Date.UTC(year, month - 1, day))
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day ? date : null
  }

  #today() {
    const now = new Date()
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
  }

  #ageBetween(joined, today) {
    let years = today.getUTCFullYear() - joined.getUTCFullYear()
    let anniversary = this.#dateInYear(joined, joined.getUTCFullYear() + years)
    if (anniversary > today) {
      years -= 1
      anniversary = this.#dateInYear(joined, joined.getUTCFullYear() + years)
    }

    let months = today.getUTCMonth() - anniversary.getUTCMonth()
    if (today.getUTCDate() < anniversary.getUTCDate()) months -= 1
    const monthiversary = this.#addMonths(anniversary, months)

    return { years, months, days: this.#daysBetween(monthiversary, today) }
  }

  #nextAnniversary(joined, today) {
    let anniversary = this.#dateInYear(joined, today.getUTCFullYear())
    if (anniversary <= today) anniversary = this.#dateInYear(joined, today.getUTCFullYear() + 1)

    return anniversary
  }

  #dateInYear(date, year) {
    return new Date(Date.UTC(year, date.getUTCMonth(), Math.min(date.getUTCDate(), this.#daysInMonth(year, date.getUTCMonth()))))
  }

  #addMonths(date, months) {
    const targetMonth = date.getUTCMonth() + months
    const year = date.getUTCFullYear() + Math.floor(targetMonth / 12)
    const month = ((targetMonth % 12) + 12) % 12
    return new Date(Date.UTC(year, month, Math.min(date.getUTCDate(), this.#daysInMonth(year, month))))
  }

  #daysInMonth(year, month) {
    return new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
  }

  #daysBetween(startDate, endDate) {
    return Math.round((endDate.getTime() - startDate.getTime()) / DAY_IN_MILLISECONDS)
  }

  #showError(message) {
    this.statusTarget.textContent = message
    this.statusTarget.classList.add("text-red-300")
    this.resultTarget.classList.add("hidden")
  }

  #number(value) {
    return new Intl.NumberFormat(this.element.ownerDocument.body.lang).format(value)
  }
}
